// src/features/tournament/useTournamentNavigation.js
import { useCallback } from 'react';
import timeSyncService from '../../services/TimeSyncService';

export const useTournamentNavigation = ({
  stateRef,
  setTournamentState,
  syncTimerToDB,
  startWorker,
  stopWorker,
  resetWorker,
  timeStringToSeconds
}) => {
  const setLevel = useCallback((index, isManual = true) => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const rows = currentState.structure.levels;
    if (!rows) return;

    const newIndex = Math.max(0, Math.min(index, rows.length - 1));
    const levelTime = timeStringToSeconds(rows[newIndex]?.time);

    const newTimerState = {
      ...currentState.timer_state,
      rowIndex: newIndex,
      remaining: levelTime,
      endsAt: null,
      pausedAt: levelTime
    };

    if (currentState.timer_state.state !== 'play' && levelTime > 0) {
      newTimerState.state = 'pause';
      newTimerState.pausedAt = levelTime;
    } else if (currentState.timer_state.state === 'play') {
      const newEndsAt = new Date(timeSyncService.getNow() + levelTime * 1000).toISOString();
      newTimerState.endsAt = newEndsAt;
      newTimerState.pausedAt = null;
    }

    setTournamentState(prev => ({ ...prev, timer_state: newTimerState }));

    if (isManual) syncTimerToDB(newTimerState);

    resetWorker(levelTime);
    if (newTimerState.state === 'play') {
      startWorker(levelTime);
    }
  }, [setTournamentState, syncTimerToDB, resetWorker, startWorker, stateRef, timeStringToSeconds]);

  const nextLevel = useCallback((options = { isManual: true }) => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const rows = currentState.structure.levels;
    const rowIndex = currentState.timer_state.rowIndex;
    if (!rows) return;

    const nextIndex = (rowIndex ?? -1) + 1;
    if (nextIndex < rows.length) {
      setLevel(nextIndex, options.isManual);
    } else {
      const newTimerState = {
        ...currentState.timer_state,
        state: 'stop',
        remaining: 0,
        rowIndex: rows.length - 1,
        endsAt: null,
        pausedAt: 0
      };
      setTournamentState(prev => ({ ...prev, timer_state: newTimerState }));
      syncTimerToDB(newTimerState);
      stopWorker();
    }
  }, [stateRef, setLevel, setTournamentState, syncTimerToDB, stopWorker]);

  const prevLevel = useCallback(() => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const rowIndex = currentState.timer_state.rowIndex;
    const prevIndex = rowIndex - 1;
    if (prevIndex >= 0) setLevel(prevIndex);
  }, [stateRef, setLevel]);

  const resetToRowStart = useCallback(() => {
    const currentState = stateRef.current;
    if (!currentState) return;

    const rows = currentState.structure.levels;
    const rowIndex = currentState.timer_state.rowIndex;

    if (rows && rows[rowIndex]) {
      const levelTime = timeStringToSeconds(rows[rowIndex].time);
      const newTimerState = {
        ...currentState.timer_state,
        remaining: levelTime,
        pausedAt: levelTime,
        endsAt: null
      };

      if (currentState.timer_state.state === 'play') {
        const newEndsAt = new Date(timeSyncService.getNow() + levelTime * 1000).toISOString();
        newTimerState.endsAt = newEndsAt;
        newTimerState.pausedAt = null;
      }

      setTournamentState(prev => ({ ...prev, timer_state: newTimerState }));
      syncTimerToDB(newTimerState);
      resetWorker(levelTime);
      if (currentState.timer_state.state === 'play') {
        startWorker(levelTime);
      }
    }
  }, [stateRef, setTournamentState, syncTimerToDB, resetWorker, startWorker, timeStringToSeconds]);

  return {
    setLevel,
    nextLevel,
    prevLevel,
    resetToRowStart
  };
};
